import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// X-Skip-Error-Redirect tells AuthInterceptor to let HTTP errors propagate
// to .subscribe(error: ...) instead of redirecting the user to the global
// error page; the JST overlay renders its own inline error states.
//
// X-Silent-Request opts out of SpinnerInterceptor so the global page-wide
// spinner overlay doesn't appear during the JST upload — the overlay panel
// already shows its own inline parsing / saving spinners.
const SILENT_REDIRECTLESS_HEADERS = new HttpHeaders({
  'X-Skip-Error-Redirect': 'true',
  'X-Silent-Request': 'true'
});

/**
 * Per-row shapes for each parsed JST sub-array. These mirror the field
 * subset that the confirmation screen reads (the LLM populates more
 * fields than we display, but the displayed ones are typed strictly so
 * the overlay's formatters don't need unsafe casts). Source of truth
 * lives in apps/cpd-subgraph/src/app/ai/schemas/parse-jst.schema.ts;
 * keep this file in sync if the schema's display-affecting fields move.
 */
export interface OccupationRow {
  mocCode?: string | null;
  mocTitle?: string | null;
  branchCode?: string | null;
  aceIdentifier?: string | null;
  dateStart?: string | null;
  dateEnd?: string | null;
  description?: string | null;
  creditRecommendations?: AceCreditRecommendation[] | null;
}

/**
 * One ACE credit recommendation for a learning experience.
 *
 * This is the thing a registrar is actually asked to accept: a named subject,
 * a number of credits and the level they sit at. It comes from
 * `api.ace_exhibits`, joined on the ACE identifier the transcript prints, and
 * NOT from the transcript itself -- a JST very often names the course and
 * leaves the recommendation to the ACE registry.
 */
export interface AceCreditRecommendation {
  subject: string;
  credits?: number | null;
  level?: string | null;
}

export interface CourseRow {
  creditRecommendations?: AceCreditRecommendation[] | null;
  courseId?: string | null;
  courseTitle?: string | null;
  aceIdentifier?: string | null;
  aceVersionNumber?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  hours?: number | null;
  aceCreditRecLower?: number | null;
  aceCreditRecUpper?: number | null;
  aceCreditType?: string | null;
  location?: string | null;
}

export interface TestRow {
  testCategory?: string | null;
  testTitle?: string | null;
  dateTaken?: string | null;
  score?: string | number | null;
  subScore1?: string | number | null;
  subScore2?: string | number | null;
  verbalScore?: string | number | null;
  dlptListeningScore?: string | number | null;
  dlptReadingScore?: string | number | null;
  dlptSpeakingScore?: string | number | null;
}

/**
 * Parsed JST payload returned from the dryRun upload. The shape mirrors
 * the JSTData type in apps/cpd-subgraph/src/app/ai/schemas/parse-jst.schema.ts.
 */
export interface JstParsedData {
  personalInfo?: {
    name?: string | null;
    branch?: string | null;
    component?: string | null;
    rank?: { title?: string | null; abbreviation?: string | null; payGrade?: string | null } | null;
    aceId?: string | null;
    aceVersion?: string | null;
    issueDate?: string | null;
    serviceStart?: string | null;
    serviceEnd?: string | null;
  } | null;
  militaryOccupations?: OccupationRow[];
  militaryCourses?: CourseRow[];
  collegeLevelTests?: TestRow[];
}

export interface JstDryRunResponse {
  success: boolean;
  message: string;
  jst_upload_id: number;
  data: JstParsedData;
}

export interface JstConfirmResponse {
  success: boolean;
  jst_upload_id: number;
}

/**
 * Read-back of the most recent confirmed JST upload. Either {success:false}
 * (no completed upload exists) or {success:true} with the parsed data so the
 * sidebar can render its "already uploaded" state without re-parsing.
 */
export type JstLatestResponse =
  | { success: false }
  | { success: true; jst_upload_id: number; data: JstParsedData };

/**
 * Remaining allowance for BILLABLE parses (each new transcript costs a Bedrock
 * call). Re-submitting a byte-identical file replays the stored parse and is not
 * counted, so these are a floor on what the user can still do.
 */
export interface JstUploadLimitResponse {
  dailyLimit: number;
  dailyRemaining: number;
  resetTime: string;
  periodLimit: number;
  periodRemaining: number;
  periodDays: number;
}

/**
 * One row of the learner's JST upload history, for the documents list.
 * Mirrors JstUploadListItem in
 * apps/cpd-subgraph/src/app/ai/parse-jst/parse-jst.service.ts.
 */
export interface JstUploadListItem {
  jstUploadId: number;
  uploadedAt: string;
  branchHint: string | null;
  /** false once a later upload replaced this one; it no longer counts toward credit. */
  isActive: boolean;
  courseCount: number;
  occupationCount: number;
  testCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class JstApiService {
  // The auth interceptor prepends environment.apiUrlBase to relative URLs
  // (see core-module/interceptors/auth.interceptor.ts) so paths here are
  // relative to /api/.
  private readonly baseUrl = 'jst';

  constructor(private readonly http: HttpClient) {}

  // TODO: Slice 3+ — pass the user's selected branch as a hint to the
  // backend so the parser can prime branch normalization for unbranded
  // / unofficial JSTs. Today the controller has no @Body() / @UploadedFiles()
  // for extra form fields, so any branchHint we'd append here would be
  // silently discarded. Until the backend grows that parameter, the
  // dropdown stays purely a "we know your branch from your profile"
  // visual; we don't send the value.
  public uploadDryRun(file: File): Observable<JstDryRunResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<JstDryRunResponse>(
      `${this.baseUrl}/upload?dryRun=true`,
      formData,
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  public confirm(
    jstUploadId: number,
    confirmedData: JstParsedData
  ): Observable<JstConfirmResponse> {
    return this.http.post<JstConfirmResponse>(
      `${this.baseUrl}/confirm`,
      {
        jst_upload_id: jstUploadId,
        confirmed_data: confirmedData
      },
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  public getLatestJst(): Observable<JstLatestResponse> {
    return this.http.get<JstLatestResponse>(
      `${this.baseUrl}/latest`,
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  public getUploadLimit(): Observable<JstUploadLimitResponse> {
    return this.http.get<JstUploadLimitResponse>(
      `${this.baseUrl}/upload-limit`,
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  /**
   * Every confirmed JST upload, newest first, for the documents list.
   *
   * Distinct from getLatestJst(): that returns the ONE active upload with its
   * full parsed payload; this returns many lightweight rows. Superseded uploads
   * come back flagged `isActive: false` rather than omitted, which is what lets
   * the list explain a changed credit total.
   */
  public listUploads(): Observable<JstUploadListItem[]> {
    return this.http.get<JstUploadListItem[]>(
      `${this.baseUrl}/uploads`,
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }

  /**
   * One upload by id, for opening a row of the documents list. Works for
   * superseded uploads; returns { success: false } when the caller has no such
   * completed upload.
   */
  public getUploadById(jstUploadId: number): Observable<JstLatestResponse> {
    return this.http.get<JstLatestResponse>(
      `${this.baseUrl}/uploads/${jstUploadId}`,
      { headers: SILENT_REDIRECTLESS_HEADERS }
    );
  }
}
